import { Link } from "react-router-dom";
import "./About.css";
import { Helmet } from "react-helmet-async";

const About = ({ dark }) => {
  return (
    <div className={`about-page ${dark ? "dark" : "light"}`}>
      <Helmet>
        <title>About FadaSports - Next Generation Sports Intelligence Platform</title>
        <meta name="description" content="FadaSports is a next-generation sports media and analytics platform delivering football statistics, match analysis, player insights, and real-time sports intelligence." />
        <meta name="keywords" content="about FadaSports, sports platform, football analytics, sports intelligence, sports media" />
        <meta name="author" content="FadaSports" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="About FadaSports" />
        <meta property="og:description" content="Learn about FadaSports - the next generation sports intelligence platform." />
        <meta property="og:image" content="https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="FadaSports" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@FadaSports" />
      </Helmet>
      
      <section className="about-hero">
        <div className="about-overlay">
          <h1>About FadaSports</h1>

          <p>
            Sports is bigger than scores. It is emotion, numbers, moments,
            patterns, history, and the stories that shape generations.
            FadaSports exists to help fans see the full picture.
          </p>

          <Link to="/livescore">
            <button>Explore Football</button>
          </Link>
        </div>
      </section>

      <section className="about-section">
        <div className="about-grid">
          
          <div className="about-card">
            <h2>Who We Are</h2>

            <p>
              FadaSports is a next generation sports media and statistics
              platform built for people who want more than headlines and
              surface level updates.
            </p>

            <p>
              We give sports fans, analysts, bettors, creators, and decision
              makers access to deeper insights, richer context, and the kind
              of data that transforms how the game is understood.
            </p>

            <p>
              The most powerful stories in sports are often hidden beneath
              the surface — the numbers behind a performance, the patterns
              behind a winning run, the context behind a headline, and the
              small details that reveal the bigger story.
            </p>
          </div>

          <div className="about-card">
            <h2>What We Deliver</h2>

            <ul>
              <li>📊 Comprehensive sports statistics</li>
              <li>⚽ Detailed performance insights</li>
              <li>📰 Trusted sports journalism</li>
              <li>📈 Intelligent data-driven analysis</li>
              <li>🎯 Smarter sports decision making</li>
              <li>🌍 Coverage across global competitions</li>
              <li>📱 Responsive experience on all devices</li>
              <li>🌙 Beautiful dark and light mode support</li>
            </ul>
          </div>

        </div>
      </section>

      <section className="about-story">
        <div className="story-content">
          
          <div className="story-left">
            <h2>Why We Exist</h2>

            <p>
              Modern sports audiences deserve better. Too much sports coverage
              today is built for speed, not depth. Too much data is scattered,
              difficult to understand, or disconnected from real meaning.
            </p>

            <p>
              We built FadaSports to change that. Our mission is to create a
              platform where sports intelligence becomes accessible,
              engaging, and actionable for everyone.
            </p>

            <p>
              A place where data meets storytelling. Where analysis meets
              entertainment. Where fans become smarter, sharper, and more
              connected to the games they love.
            </p>
          </div>

          <div className="story-right">
            <div className="stats-box">
              <h3>What Makes FadaSports Different</h3>

              <div className="stat-item">
                <strong>Data With Context</strong>
                <span>Statistics explained with real meaning</span>
              </div>

              <div className="stat-item">
                <strong>Insightful Analysis</strong>
                <span>Breaking down the “why” behind performances</span>
              </div>

              <div className="stat-item">
                <strong>Trusted Journalism</strong>
                <span>Stories, reports, and perspectives that matter</span>
              </div>

              <div className="stat-item">
                <strong>Sports Intelligence</strong>
                <span>Helping fans understand the game deeper</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="about-features">
        
        <h2>Inside The FadaSports Experience</h2>

        <div className="features-grid">

          <div className="feature-box">
            <h3>Comprehensive Statistics</h3>

            <p>
              Access detailed performance data across leagues, clubs,
              players, matches, and competitions worldwide.
            </p>
          </div>

          <div className="feature-box">
            <h3>Insightful Analysis</h3>

            <p>
              Discover context-driven sports analysis that explains the
              patterns, tactics, and trends behind the numbers.
            </p>
          </div>

          <div className="feature-box">
            <h3>Sports Journalism</h3>

            <p>
              Read fresh stories, reports, opinions, and perspectives that
              go beyond surface-level sports coverage.
            </p>
          </div>

          <div className="feature-box">
            <h3>Smarter Decision Making</h3>

            <p>
              Gain insights that empower fans, creators, analysts, and sports
              enthusiasts to think deeper and make smarter sports decisions.
            </p>
          </div>

          <div className="feature-box">
            <h3>Global Competition Coverage</h3>

            <p>
              Follow clubs, leagues, competitions, players, and sporting
              events from across the world in one experience.
            </p>
          </div>

          <div className="feature-box">
            <h3>The Future Of Sports Intelligence</h3>

            <p>
              We are building a growing ecosystem designed to redefine how
              sports content is consumed across Africa and beyond.
            </p>
          </div>

        </div>
      </section>

      <section className="about-vision">
        <div className="vision-box">

          <h2>Our Vision</h2>

          <p>
            To become Africa’s leading global sports intelligence platform,
            shaping the future of sports media through innovation,
            credibility, and insight.
          </p>

          <p>
            We are building more than a platform. We are building the future
            home of sports understanding — a place where passion meets
            precision, where every stat tells a story, and where every fan
            gets closer to the game.
          </p>

          <p>
            This is sports, fully understood. This is FadaSports.
          </p>

        </div>
      </section>

      <section className="about-contact">
        
        <h2>Join The FadaSports Community</h2>

        <p>
          Explore deeper sports insights, follow the stories behind the
          numbers, and experience a smarter way to understand the games you
          love with FadaSports.
        </p>

        <Link to="/login">
          <button>Start Exploring</button>
        </Link>

      </section>

    </div>
  );
};

export default About;